import { db } from "../firebase";
import { doc, setDoc, updateDoc, getDoc, deleteField } from "firebase/firestore";

export const addTrip = async (tripData) => {
  try {
    // Reference to the packages collection and all_packages document
    const packagesRef = doc(db, "packages", "all_packages");

    // Check if the all_packages document exists, if not create it
    const docSnap = await getDoc(packagesRef);
    if (!docSnap.exists()) {
      await setDoc(packagesRef, {});
    }

    // Ensure tripId is valid
    if (!tripData.tripId) {
      throw new Error("Trip ID is missing or invalid.");
    }

    // Ensure itinerary days have meals property
    const itineraryWithMeals = {};
    Object.entries(tripData.Itinerary || {}).forEach(([dayKey, dayValue]) => {
      if (typeof dayValue === 'string') {
        itineraryWithMeals[dayKey] = { title: "", description: dayValue, meals: { B: false, L: false, D: false } };
      } else {
        itineraryWithMeals[dayKey] = {
          title: dayValue.title || "",
          description: dayValue.description || "",
          meals: dayValue.meals || { B: false, L: false, D: false }
        };
      }
    });

    // Create trip object with the specified structure, ensuring defaults
    const tripObject = {
      [tripData.tripId]: {
        tripname: tripData.tripname || "", // Default to empty string
        description: tripData.description || "",
        display_price: tripData.display_price || "",
        starting_price: tripData.starting_price || {},
        days: Number(tripData.days) || 0, // Default to 0 if NaN
        nights: Number(tripData.nights) || 0, // Default to 0 if NaN
        location: tripData.location || "", // Default to empty string
        Itinerary: itineraryWithMeals, // Always save with meals
        inclusions: tripData.inclusions || [], // Default to empty array
        exclusions: tripData.exclusions || [], // Default to empty array
        category: tripData.category || "", // Default to empty string
        cover_image_url: tripData.cover_image_url || "",
        additional_image_url: tripData.additional_image_url || [],
        brochure_url: tripData.brochure_url || "",
        batches: tripData.batches || {},
        pickup_points: tripData.pickup_points || {},
        dropping_points: tripData.dropping_points || {},
        notes: tripData.notes || [],
        status: tripData.status || "Active" // Default to Active status
      }
    };

    // Update the all_packages document with the new trip
    await updateDoc(packagesRef, tripObject);

    return { success: true, message: "Trip added successfully!" };
  } catch (error) {
    // console.error("Error adding trip: ", error);
    return { success: false, message: error.message };
  }
};

export const generateTripId = async (tripName, days, nights) => {
  try {
    if (!tripName || !days || !nights) {
      return ""; // Return empty if required parameters are missing
    }

    // Format: Full trip name (converted to URL-friendly format) + Days + "N" + Nights
    const nameSlug = tripName.trim()
                            .toLowerCase()
                            .replace(/[^a-z0-9\s-]/g, "") // Remove special chars except spaces and hyphens
                            .replace(/\s+/g, "-");         // Replace spaces with hyphens

    const formattedDays = days.toString().padStart(2, '0');
    const formattedNights = nights.toString().padStart(2, '0');

    const tripId = `${nameSlug}-${formattedDays}d${formattedNights}n`;

    return tripId;
  } catch (error) {
    // console.error("Error generating trip ID: ", error);
    return "";
  }
};

/**
 * Check if a trip ID already exists
 * @param {string} tripId - The trip ID to check
 * @returns {Promise<boolean>} - True if the trip ID exists, false otherwise
 */
export const checkTripIdExists = async (tripId) => {
  try {
    // Reference to the packages collection and all_packages document
    const packagesRef = doc(db, "packages", "all_packages");

    // Get the current document
    const docSnap = await getDoc(packagesRef);

    if (!docSnap.exists()) {
      return false;
    }

    // Check if the trip ID exists in the document
    const data = docSnap.data();
    return !!data[tripId];
  } catch (error) {
    // console.error("Error checking trip ID: ", error);
    return false;
  }
};

/**
 * Update an existing trip in Firestore
 * @param {string} tripId - The ID of the trip to update
 * @param {Object} updatedData - The updated trip data
 * @returns {Promise<Object>} Result object with success status and message
 */
export const updateTrip = async (tripId, updatedData) => {
  try {
    // Reference to the packages collection and all_packages document
    const packagesRef = doc(db, "packages", "all_packages");

    // Check if the document exists
    const docSnap = await getDoc(packagesRef);
    if (!docSnap.exists()) {
      return { success: false, message: "Trips database not found!" };
    }

    // Get the current data
    const tripsData = docSnap.data();
    const existingTrip = tripsData[tripId];

    // Check if the trip exists
    if (!existingTrip) {
      return { success: false, message: `Trip with ID ${tripId} not found!` };
    }

    // Process pricing data for Firestore
    let pricingForFirestore = {};
    if (updatedData.starting_price && typeof updatedData.starting_price === 'object') {
       pricingForFirestore = updatedData.starting_price;
    } else if (existingTrip.starting_price) {
       pricingForFirestore = existingTrip.starting_price;
    }

    // Ensure itinerary days have meals property
    let itineraryWithMeals = {};
    if (updatedData.Itinerary && typeof updatedData.Itinerary === 'object') {
      Object.entries(updatedData.Itinerary).forEach(([dayKey, dayValue]) => {
        if (typeof dayValue === 'string') {
          itineraryWithMeals[dayKey] = { title: "", description: dayValue, meals: { B: false, L: false, D: false } };
        } else {
          itineraryWithMeals[dayKey] = {
            title: dayValue.title || "",
            description: dayValue.description || "",
            meals: dayValue.meals || { B: false, L: false, D: false }
          };
        }
      });
    } else if (existingTrip.Itinerary) {
      Object.entries(existingTrip.Itinerary).forEach(([dayKey, dayValue]) => {
        if (typeof dayValue === 'string') {
          itineraryWithMeals[dayKey] = { title: "", description: dayValue, meals: { B: false, L: false, D: false } };
        } else {
          itineraryWithMeals[dayKey] = {
            title: dayValue.title || "",
            description: dayValue.description || "",
            meals: dayValue.meals || { B: false, L: false, D: false }
          };
        }
      });
    }

    // Determine the final trip ID (handles potential ID change)
    const newTripId = updatedData.tripId && updatedData.tripId !== tripId ? updatedData.tripId : tripId;
    const isChangingId = newTripId !== tripId;
    
    // Ensure image URLs are properly handled
    const coverImageUrl = updatedData.cover_image_url !== undefined ? updatedData.cover_image_url : existingTrip.cover_image_url || "";
    const brochureUrl = updatedData.brochure_url !== undefined ? updatedData.brochure_url : existingTrip.brochure_url || "";
    
    // Handle additional images with special care
    let additionalImages = [];
    if (updatedData.additional_image_url !== undefined) {
      additionalImages = Array.isArray(updatedData.additional_image_url) 
        ? updatedData.additional_image_url.filter(url => url && url.trim() !== "") 
        : [updatedData.additional_image_url].filter(url => url && url.trim() !== "");
    } else {
      additionalImages = Array.isArray(existingTrip.additional_image_url) 
        ? existingTrip.additional_image_url 
        : existingTrip.additional_image_url ? [existingTrip.additional_image_url] : [];
    }

    // Create updated trip object with robust defaults
    const updatedTripPayload = {
      tripname: updatedData.tripname !== undefined ? updatedData.tripname : (existingTrip.tripname || ""),
      description: updatedData.description !== undefined ? updatedData.description : (existingTrip.description || ""),
      display_price: updatedData.display_price !== undefined ? updatedData.display_price : (existingTrip.display_price || ""),
      starting_price: pricingForFirestore, // Already processed above
      days: updatedData.days !== undefined ? Number(updatedData.days) || 0 : (existingTrip.days || 0),
      nights: updatedData.nights !== undefined ? Number(updatedData.nights) || 0 : (existingTrip.nights || 0),
      location: updatedData.location !== undefined ? updatedData.location : (existingTrip.location || ""),
      Itinerary: itineraryWithMeals,
      inclusions: updatedData.inclusions !== undefined ? updatedData.inclusions.filter(item => item && item.trim() !== "") : (existingTrip.inclusions || []),
      exclusions: updatedData.exclusions !== undefined ? updatedData.exclusions.filter(item => item && item.trim() !== "") : (existingTrip.exclusions || []),
      category: updatedData.category !== undefined ? updatedData.category : (existingTrip.category || ""),
      cover_image_url: coverImageUrl,
      additional_image_url: additionalImages,
      brochure_url: brochureUrl,
      batches: updatedData.batches !== undefined ? updatedData.batches : (existingTrip.batches || {}),
      pickup_points: updatedData.pickup_points !== undefined ? updatedData.pickup_points : (existingTrip.pickup_points || {}),
      dropping_points: updatedData.dropping_points !== undefined ? updatedData.dropping_points : (existingTrip.dropping_points || {}),
      notes: updatedData.notes !== undefined ? updatedData.notes.filter(item => item && item.trim() !== "") : (existingTrip.notes || []),
      status: updatedData.status !== undefined ? updatedData.status : (existingTrip.status || "Draft")
    };

    if (isChangingId) {
      // If ID is changing, add the new entry first
      await updateDoc(packagesRef, { [newTripId]: updatedTripPayload });

      // Then remove the old ID entry
      await updateDoc(packagesRef, {
        [tripId]: deleteField()
      });

      return { success: true, message: "Trip updated with new ID successfully!" };
    } else {
      // Update the document with the modified trip (same ID)
      await updateDoc(packagesRef, { [tripId]: updatedTripPayload });

      return { success: true, message: "Trip updated successfully!" };
    }
  } catch (error) {
    // console.error("Error updating trip: ", error);
    // Check for specific Firestore errors if needed
    if (error.code === 'invalid-argument') {
        return { success: false, message: `Invalid data sent to Firestore: ${error.message}` };
    }
    return { success: false, message: error.message };
  }
};

/**
 * Delete a trip from Firestore
 * @param {string} tripId - The ID of the trip to delete
 * @returns {Promise<Object>} Result object with success status and message
 */
export const deleteTrip = async (tripId) => {
  try {
    // Reference to the packages collection and all_packages document
    const packagesRef = doc(db, "packages", "all_packages");

    // Check if the document exists
    const docSnap = await getDoc(packagesRef);
    if (!docSnap.exists()) {
      return { success: false, message: "Trips database not found!" };
    }

    // Get the current data
    const tripsData = docSnap.data();

    // Check if the trip exists
    if (!tripsData[tripId]) {
      return { success: false, message: `Trip with ID ${tripId} not found!` };
    }

    // Create a new object without the trip to delete
    const updatedTripsData = { ...tripsData };
    delete updatedTripsData[tripId];

    // Update the document with the trip removed
    await setDoc(packagesRef, updatedTripsData);

    return { success: true, message: "Trip deleted successfully!" };
  } catch (error) {
    // console.error("Error deleting trip: ", error);
    return { success: false, message: error.message };
  }
};
